import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost, useFetchResource } from '@app/src/api/fetchHooks'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { SortOrder } from '@app/src/types/filter'
import type { RiskTableView } from '@app/src/types/organizations'
import type { OrganizationRiskSetting, Risk, RiskStatus, RiskType } from '@app/src/types/resourceExplorer'
import { sortAlphabetically } from '@app/src/utils/helpers'
import { ONE_MINUTE_MS } from '@app/src/wf-constants'
import { useMemo } from 'react'

export interface ProviderRiskIndex {
  riskType: RiskType
  isActivated: boolean
  status: RiskStatus | undefined
}

export const buildProviderRiskIndices = (
  riskSettings: OrganizationRiskSetting[] | undefined,
  countryRisks: Risk[] | undefined,
): ProviderRiskIndex[] => {
  const indices = (riskSettings ?? []).map(setting => ({
    riskType: setting.riskType,
    isActivated: setting.isActivated,
    status: countryRisks?.find(risk => risk.riskTypeId === setting.riskType.id)?.status,
  }))
  const sorted = sortAlphabetically(indices, index => index.riskType.name)
  return [...sorted.filter(index => index.isActivated), ...sorted.filter(index => !index.isActivated)]
}

export const useProviderRiskIndices = (
  providerId: number | undefined,
  assessmentTemplateId: number,
  enabled: boolean,
) => {
  const { items: riskScreeningItems, isLoading: isLoadingRisks } = useFetchCollectionWithPost<RiskTableView>({
    key: [FetchKey.RiskScreening, providerId],
    endpoint: endpoints.riskScreeningAllRisks,
    payload: {
      filter: [
        { name: 'isAllRisk', filters: [{ value: true, operator: Operators.EqualTo }] },
        { name: 'id', filters: [{ value: providerId, operator: Operators.EqualTo }] },
      ],
      include: ['country.risks.riskType'],
      sort: { order: SortOrder.ASCENDING, target: 'name' },
      pagination: { pageNumber: 1, itemsPerPage: 1 },
    },
    options: { enabled: enabled && Boolean(providerId), staleTime: ONE_MINUTE_MS },
  })

  const { data: riskSettings, isLoading: isLoadingSettings } = useFetchResource<OrganizationRiskSetting[]>({
    key: [FetchKey.AssessmentTemplateRiskIndices, assessmentTemplateId],
    endpoint: endpoints.assessmentTemplateRiskIndices(assessmentTemplateId),
    options: { enabled, staleTime: ONE_MINUTE_MS },
  })

  const riskIndices = useMemo(
    () => buildProviderRiskIndices(riskSettings, riskScreeningItems[0]?.parentObject?.country?.risks),
    [riskSettings, riskScreeningItems],
  )

  return { riskIndices, isLoading: isLoadingRisks || isLoadingSettings }
}
